import { ref, computed } from 'vue'
import axios, { AxiosError } from 'axios'
import type { GroupRoleMapping } from '@/utils/roles'

// The engine only ever persists these three process types.
export type OnboardingType = 'INITIAL' | 'SELF_SERVICE' | 'OIDC_RECOVERY'

// Legacy change types remain accepted by the API and are normalized onto
// SELF_SERVICE server-side. They only exist so older links and the internal
// provisioning API keep working.
export type LegacyOnboardingType =
  'ADD_TENANT' | 'CHANGE_TENANT' | 'CHANGE_PERMISSIONS' | 'CHANGE_OIDC'

export type RequestedOnboardingType = OnboardingType | LegacyOnboardingType

const LEGACY_TYPE_ALIASES: readonly string[] = [
  'ADD_TENANT',
  'CHANGE_TENANT',
  'CHANGE_PERMISSIONS',
  'CHANGE_OIDC',
]

// normalizeProcessType mirrors the server-side normalization so a process that
// still carries a legacy type renders the unified self-service copy.
export function normalizeProcessType(type?: string | null): OnboardingType | null {
  if (!type) return null
  if (type === 'INITIAL' || type === 'SELF_SERVICE' || type === 'OIDC_RECOVERY') {
    return type
  }
  if (LEGACY_TYPE_ALIASES.includes(type)) {
    return 'SELF_SERVICE'
  }
  return null
}

/**
 * Normalizes an email regex input string.
 * If the input matches a plain domain format (e.g. mbcom.de, @mbcom.de, sub.domain.de),
 * it is automatically converted into an anchored regular expression: ^[A-Za-z0-9._%+-]+@<escaped_domain>$
 * If the input is already a custom regular expression, it is returned trimmed.
 */
export function normalizeEmailRegexInput(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  const domainMatch = trimmed.match(
    /^@?([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+)$/,
  )
  if (domainMatch) {
    const domain = domainMatch[1]
    const escapedDomain = domain.replace(/\./g, '\\.')
    return `^[A-Za-z0-9._%+-]+@${escapedDomain}$`
  }
  return trimmed
}

/** A single step of the onboarding wizard. */
export interface OnboardingStepDef {
  /** Stable identifier of the step, independent of its position. */
  key: string
  /** German title shown in the wizard navigation. */
  title: string
}

// Single source of truth for the wizard step order. Components that navigate to
// a specific step resolve its index from this list by key instead of
// hardcoding numbers, so inserting or reordering a step stays safe.
export const onboardingWizardSteps: readonly OnboardingStepDef[] = [
  { key: 'account', title: 'Konto' },
  { key: 'loginMode', title: 'Login-Methode' },
  { key: 'oidc', title: 'Authentifizierung' },
  { key: 'verification', title: 'Überprüfung' },
  { key: 'tenants', title: 'Mandanten' },
  { key: 'permissions', title: 'Berechtigungen' },
  { key: 'review', title: 'Abschluss' },
]

// getWizardSteps derives the step list of a process. The login method step only
// exists while local users may be chosen and the process may change the login
// method (portal admins only), and local users need neither an
// identity provider nor its verification login.
export function getWizardSteps(
  loginMode?: 'oidc' | 'local',
  localLoginEnabled?: boolean,
  canChangeLoginMode?: boolean,
): readonly OnboardingStepDef[] {
  return onboardingWizardSteps.filter((step) => {
    if (step.key === 'loginMode') return localLoginEnabled === true && canChangeLoginMode === true
    if (step.key === 'oidc' || step.key === 'verification') return loginMode !== 'local'
    return true
  })
}

export type OnboardingStatus =
  | 'CREATED'
  | 'INVITED'
  | 'STARTED'
  | 'OIDC_CONFIGURED'
  | 'OIDC_VERIFIED'
  | 'TENANTS_CONFIGURED'
  | 'PERMISSIONS_CONFIGURED'
  | 'READY_FOR_PROVISIONING'
  | 'PROVISIONING'
  | 'COMPLETED'
  | 'FAILED'
  | 'EXPIRED'
  | 'REVOKED'

// User-facing German copy for a provisioning process. Only the INITIAL type is
// an actual onboarding; every other type is a self-service configuration change
// of an already onboarded customer and must not mention "Onboarding".
export interface ProcessLabels {
  /** Short title of the flow (wizard header, landing badge). */
  title: string
  /** One-line subtitle describing the flow. */
  subtitle: string
  /** Headline shown on the completion screen. */
  completionTitle: string
  /** Body text shown on the completion screen. */
  completionText: string
  /** Heading shown at the top of the invitation landing page. */
  landingHeading: string
  /** Main paragraph explaining the invitation on the landing page. */
  landingDescription: string
  /** Secondary hint describing what happens in the wizard. */
  landingHint: string
  /** Call-to-action label that starts the flow from the invitation. */
  landingCta: string
}

const CHANGE_SUBTITLE = 'Konfigurationsänderung Ihres ConnectCLOUD Portal-Zugangs'
const CHANGE_COMPLETION_TITLE = 'Änderung erfolgreich abgeschlossen!'
const CHANGE_COMPLETION_TEXT =
  'Ihre Konfiguration wurde als aktive Revision bereitgestellt. Die Sitzung wurde ordnungsgemäß beendet.'
const CHANGE_LANDING_HEADING = 'ConnectCLOUD Konfiguration'
const CHANGE_LANDING_DESCRIPTION =
  'Sie wurden als Administrator eingeladen, eine Konfigurationsänderung für Ihre Organisation vorzunehmen.'
const CHANGE_LANDING_CTA = 'Konfigurationsänderung starten'

// Human-readable German label of a process type, used in overviews and dialogs.
export const processTypeNames: Record<OnboardingType, string> = {
  INITIAL: 'Erstmaliges Onboarding',
  SELF_SERVICE: 'Konfiguration bearbeiten',
  OIDC_RECOVERY: 'Anmeldung wiederherstellen',
}

// Typed lookup of the German copy per process type. Prefer this single map over
// scattering type conditionals across components.
export const processTypeLabels: Record<OnboardingType, ProcessLabels> = {
  INITIAL: {
    title: 'Onboarding',
    subtitle: 'Einrichtung Ihres ConnectCLOUD Portal-Zugangs',
    completionTitle: 'Onboarding erfolgreich abgeschlossen!',
    completionText:
      'Ihre Konfiguration wurde als aktive Revision bereitgestellt. Die Onboarding-Sitzung wurde ordnungsgemäß beendet.',
    landingHeading: 'ConnectCLOUD Onboarding',
    landingDescription:
      'Sie wurden als Administrator eingeladen, die Ersteinrichtung von ConnectCLOUD für Ihre Organisation abzuschließen.',
    landingHint:
      'Im folgenden Assistenten konfigurieren Sie die OIDC-Authentifizierung, legen Ihre Mandanten an und vergeben Rollen.',
    landingCta: 'Onboarding starten',
  },
  SELF_SERVICE: {
    title: 'Konfiguration bearbeiten',
    subtitle: CHANGE_SUBTITLE,
    completionTitle: CHANGE_COMPLETION_TITLE,
    completionText: CHANGE_COMPLETION_TEXT,
    landingHeading: CHANGE_LANDING_HEADING,
    landingDescription: CHANGE_LANDING_DESCRIPTION,
    landingHint:
      'Im folgenden Assistenten passen Sie Kontaktdaten, Anmeldung, Mandanten und Berechtigungen an.',
    landingCta: CHANGE_LANDING_CTA,
  },
  OIDC_RECOVERY: {
    title: 'Anmeldung wiederherstellen',
    subtitle: CHANGE_SUBTITLE,
    completionTitle: CHANGE_COMPLETION_TITLE,
    completionText: CHANGE_COMPLETION_TEXT,
    landingHeading: CHANGE_LANDING_HEADING,
    landingDescription: CHANGE_LANDING_DESCRIPTION,
    landingHint: 'Im folgenden Assistenten stellen Sie Ihre Anmeldung wieder her.',
    landingCta: CHANGE_LANDING_CTA,
  },
}

// getProcessLabels returns the German copy for the given process type. When the
// type is unknown (e.g. not loaded yet) it falls back to neutral change-flow
// wording that works for both onboarding and configuration changes.
export function getProcessLabels(type?: string | null): ProcessLabels {
  const normalized = normalizeProcessType(type)
  if (normalized) {
    return processTypeLabels[normalized]
  }
  return {
    title: 'Einrichtung',
    subtitle: CHANGE_SUBTITLE,
    completionTitle: CHANGE_COMPLETION_TITLE,
    completionText: CHANGE_COMPLETION_TEXT,
    landingHeading: 'ConnectCLOUD Einrichtung',
    landingDescription: CHANGE_LANDING_DESCRIPTION,
    landingHint: 'Im folgenden Assistenten schließen Sie die angeforderte Konfiguration ab.',
    landingCta: 'Einrichtung starten',
  }
}

export interface TenantDraft {
  id: string
  code: string
  name: string
  enabled: boolean
  allowedEmailRegex?: string[]
  groupRoleMapping?: GroupRoleMapping
  groupNameMapping?: Record<string, string>
}

export interface PermissionDraft {
  tenantName: string
  groupRoleMapping?: GroupRoleMapping
  groupNameMapping?: Record<string, string>
  allowedEmailRegex: string[]
}

export interface OIDCDraft {
  issuer: string
  clientId: string
  clientSecret?: string
  scopes?: string[]
  groupClaim?: string
  providerType?: 'entra' | 'generic' | string
}

export interface OIDCDiscoveryResult {
  issuer: string
  authorization_endpoint: string
  token_endpoint: string
  jwks_uri: string
}

export interface OnboardingRedirectUris {
  verificationCallbackUrl: string
  loginCallbackUrl: string
}

export interface OIDCVerification {
  verified: boolean
  subject: string
  email: string
  groups: string[]
  groupDetails?: GroupDetail[]
  adminProven: boolean
  verifiedAt: string
}

export interface GroupDetail {
  id: string
  displayName: string
}

// RecoveryContact is a verified administrator address reserved for security
// actions (recovery, cancel and restore links). It is deliberately separate from
// `brevoEmail`, which is a broad notification distribution list and therefore
// never receives an action link.
export interface RecoveryContact {
  email: string
  verified: boolean
  verifiedAt?: string
  addedAt: string
}

export interface CustomerDraft {
  customerNo: string
  customerName: string
  customerShortName: string
  brevoEmail: string
  brevoActive: boolean
  recoveryContacts?: RecoveryContact[]
  groupRoleMapping?: Record<string, string>
  groupNameMapping?: Record<string, string>
}

// RecoveryContactsResult is the read model returned by the recovery-contact
// endpoints.
export interface RecoveryContactsResult {
  contacts: RecoveryContact[]
  verifiedCount: number
  pendingCount: number
  invitationsSent: number
}

// RecoveryCodesResult carries the one-time recovery codes. They are shown
// exactly once; only their hashes are stored server-side.
export interface RecoveryCodesResult {
  codes: string[]
  remaining: number
  fileName: string
  fileContent: string
}

export interface ConfigurationDraft {
  customer: CustomerDraft
  oidc: OIDCDraft
  tenants: TenantDraft[]
  permissions: PermissionDraft[]
}

// EditableSections mirrors the server-side section authorization returned by
// GET /onboarding/state. The wizard always renders every step but locks the
// sections the caller may not edit; the server re-checks every write, so this is
// convenience only.
export interface EditableSections {
  contact: boolean
  oidc: boolean
  tenants: boolean
  allPermissions: boolean
  permissions?: string[]
  customerAdminMapping: boolean
  // Only portal-admin processes may switch between OIDC and local users.
  loginMode?: boolean
}

export interface OnboardingProcess {
  id: string
  customerNo: string
  type: OnboardingType
  status: OnboardingStatus
  draft: ConfigurationDraft
  verification?: OIDCVerification
  // Reported by GET /onboarding/state. Optional because older backends may not
  // include it; the UI then falls back to "everything editable".
  editableSections?: EditableSections
  // Reported by GET /onboarding/state when an OIDC client secret is already
  // stored server-side. The secret itself is sanitized and never returned, so
  // this flag lets the UI signal that a secret exists without exposing it.
  // Optional because older backends may not include it.
  clientSecretSet?: boolean
  // The recovery channel reported by GET /onboarding/state.
  recoveryContacts?: RecoveryContact[]
  recoveryContactsVerified?: number
  recoveryCodesRemaining?: number
  // Whether the activation is refused without a verified recovery contact.
  recoveryContactsRequired?: boolean
  // Login method of the draft ("oidc" or "local") and whether local users may be
  // chosen at all, as reported by GET /onboarding/state.
  loginMode?: 'oidc' | 'local'
  localLoginEnabled?: boolean
  createdAt: string
  expiresAt: string
  completedAt?: string
}

export type ProvisioningDelivery = 'SESSION' | 'EMAIL'

export interface ProvisioningStartResult {
  id?: string
  status?: OnboardingStatus
  delivery?: ProvisioningDelivery
  invitationSent?: boolean
  recipient?: string
}

export interface InvitationPeek {
  customerNo: string
  customerName?: string
  expiresAt: string
  type: OnboardingType
  status: OnboardingStatus
}

export interface AppError {
  code: string
  message: string
  details?: string
  /** Short server-generated code correlating this error with the backend log entry. */
  reference?: string
}

export function mapGermanError(err: unknown): AppError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<{
      error?: string
      code?: string
      message?: string
      reference?: string
    }>
    const status = axiosErr.response?.status
    const serverErr = axiosErr.response?.data?.error || axiosErr.response?.data?.message || ''
    const serverCode = axiosErr.response?.data?.code
    const reference = axiosErr.response?.data?.reference

    if (
      serverCode === 'STEP_UP_REQUIRED' ||
      serverCode === 'ERR_STEP_UP_REQUIRED' ||
      serverErr.includes('Step-up') ||
      serverErr.includes('erneute Authentifizierung')
    ) {
      return {
        code: 'ERR_STEP_UP_REQUIRED',
        message:
          'Für diese Aktion ist eine erneute Authentifizierung erforderlich. Bitte bestätigen Sie die Änderung erneut.',
        details: serverErr,
        reference,
      }
    }

    // The configuration is already active but creating the local users failed.
    // The session stays open, so repeating the activation is the way forward.
    if (serverCode === 'LOCAL_USERS_SETUP_FAILED') {
      return {
        code: 'ERR_LOCAL_USERS_SETUP_FAILED',
        message:
          serverErr ||
          'Die Konfiguration ist aktiv, aber die Einrichtung der lokalen Benutzer ist fehlgeschlagen. Bitte die Aktivierung wiederholen.',
        reference,
      }
    }

    // The Portal session itself is too old to count as a fresh authentication, so
    // no step-up grant can be issued without a new login.
    if (
      serverCode === 'REAUTHENTICATION_REQUIRED' ||
      serverCode === 'ERR_REAUTHENTICATION_REQUIRED'
    ) {
      return {
        code: 'ERR_REAUTHENTICATION_REQUIRED',
        message:
          'Ihre Anmeldung ist zu alt für diese sicherheitsrelevante Änderung. Bitte melden Sie sich neu an und wiederholen Sie den Vorgang.',
        details: serverErr,
        reference,
      }
    }

    if (status === 401) {
      return {
        code: 'ERR_AUTH_SESSION_EXPIRED',
        message:
          'Ihre Sitzung ist abgelaufen oder ungültig. Bitte öffnen Sie den Einladungslink erneut.',
        details: serverErr,
      }
    }

    if (status === 403) {
      if (serverErr.includes('CustomerAdmin') || serverErr.includes('Administrator-Berechtigung')) {
        return {
          code: 'ERR_AUTH_ADMIN_NOT_PROVEN',
          message:
            'Aktivierung verweigert: Es wurde keine verifizierte Administrator-Berechtigung (CustomerAdmin) für Ihr Konto nachgewiesen.',
          details: serverErr,
        }
      }
      return {
        code: 'ERR_AUTH_FORBIDDEN',
        message: 'Zugriff verweigert. Sie verfügen nicht über die erforderlichen Berechtigungen.',
        details: serverErr,
      }
    }

    if (status === 404) {
      return {
        code: 'ERR_NOT_FOUND',
        message: 'Der angeforderte Vorgang oder die Einladung wurde nicht gefunden.',
        details: serverErr,
      }
    }

    if (status === 429) {
      return {
        code: 'ERR_RATE_LIMIT',
        message:
          'Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment und versuchen Sie es erneut.',
        details: serverErr,
      }
    }

    if (serverErr.toLowerCase().includes('discovery')) {
      return {
        code: 'ERR_OIDC_DISCOVERY',
        message:
          'Die OIDC-Konfiguration konnte nicht automatisch ermittelt werden. Bitte prüfen Sie die Issuer-URL.',
        details: serverErr,
      }
    }

    if (serverErr.includes('Mandant')) {
      return {
        code: 'ERR_TENANT_CONFIG',
        message: serverErr || 'Die Mandanten-Konfiguration ist unvollständig oder fehlerhaft.',
        details: serverErr,
      }
    }

    if (serverErr) {
      return {
        code: 'ERR_API_REQUEST',
        message: serverErr,
        details: serverErr,
        reference,
      }
    }

    if (!axiosErr.response) {
      return {
        code: 'ERR_NETWORK',
        message:
          'Verbindung zum ConnectCLOUD-Server konnte nicht hergestellt werden. Bitte überprüfen Sie Ihre Internetverbindung.',
      }
    }
  }

  return {
    code: 'ERR_UNKNOWN',
    message: err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten.',
  }
}

export function useOnboarding() {
  const process = ref<OnboardingProcess | null>(null)
  const invitation = ref<InvitationPeek | null>(null)
  const discoveryResult = ref<OIDCDiscoveryResult | null>(null)
  const redirectUris = ref<OnboardingRedirectUris | null>(null)
  const loading = ref(false)
  const error = ref<AppError | null>(null)

  // Step-up state. The wizard opens without any step-up; only a real change of
  // the identity provider makes the server answer STEP_UP_REQUIRED, which sets
  // these refs so the wizard can show the confirmation dialog and retry the very
  // same save afterwards. The entered values stay in the component, so nothing
  // is lost while the grant is obtained.
  const stepUpRequired = ref(false)
  const stepUpReference = ref<string | undefined>(undefined)
  const stepUpPending = ref(false)
  const reauthenticationRequired = ref(false)

  // The one-time recovery codes are returned exactly once — by the activation
  // that first establishes the recovery channel, or by an explicit
  // regeneration. They are held in memory only so the completion screen can
  // show and offer them for download; they are never persisted client-side.
  const recoveryCodes = ref<RecoveryCodesResult | null>(null)

  // The initial onboarding and a recovery always configure the identity
  // provider. The unified self-service wizard offers the section as well, but
  // only demands a verification login when it is actually modified, which the
  // server decides from the diff.
  const requiresOIDC = computed(() => {
    if (!process.value) return true
    if (process.value.loginMode === 'local') return false
    const type = normalizeProcessType(process.value.type)
    return type === 'INITIAL' || type === 'OIDC_RECOVERY'
  })

  // The section authorization reported by the server. Without the field (older
  // backend, or a process created before section authorization existed) every
  // section stays editable, which preserves the previous behaviour.
  const editableSections = computed<EditableSections>(() => {
    const sections = process.value?.editableSections
    if (!sections) {
      return {
        contact: true,
        oidc: true,
        tenants: true,
        allPermissions: true,
        customerAdminMapping: true,
        loginMode: false,
      }
    }
    return sections
  })

  const canEditContact = computed(() => editableSections.value.contact)
  const canEditOIDC = computed(() => editableSections.value.oidc)
  const canEditTenants = computed(() => editableSections.value.tenants)

  // canEditPermissions reports whether the permissions of the given tenant may
  // be changed. Without a tenant name it reports whether any tenant is editable.
  function canEditPermissions(tenantName?: string): boolean {
    const sections = editableSections.value
    if (sections.allPermissions) return true
    const allowed = sections.permissions ?? []
    if (!tenantName) return allowed.length > 0
    return allowed.some((t) => t.trim().toLowerCase() === tenantName.trim().toLowerCase())
  }

  const canEditAnyPermission = computed(() => canEditPermissions())

  // lockedSectionHint provides the German explanation shown on a locked section.
  function lockedSectionHint(section: 'contact' | 'oidc' | 'tenants' | 'permissions'): string {
    switch (section) {
      case 'contact':
        return 'Die Benachrichtigungs-E-Mail-Adresse kann nur von einem Kundenadministrator geändert werden. Sie wird hier nur zur Information angezeigt.'
      case 'oidc':
        return 'Die Anmeldung (Identity Provider) kann nur von einem Kundenadministrator geändert werden. Sie wird hier nur zur Information angezeigt.'
      case 'tenants':
        return 'Mandanten können in diesem Vorgang nicht angelegt oder geändert werden. Sie werden hier nur zur Information angezeigt.'
      default:
        return 'Berechtigungen können nur für die eigenen Mandanten geändert werden. Alle weiteren Mandanten werden hier nur zur Information angezeigt.'
    }
  }

  const isOIDCVerified = computed(() => {
    return !!process.value?.verification?.verified
  })

  const isAdminProven = computed(() => {
    return !!process.value?.verification?.adminProven
  })

  const hasValidTenants = computed(() => {
    const tenants = process.value?.draft?.tenants
    return Array.isArray(tenants) && tenants.length > 0 && tenants.some((t) => !!t.code && !!t.name)
  })

  // A customer-level notification address must be present whenever Brevo
  // notifications are active. When notifications are disabled no address is
  // required.
  const hasNotificationEmail = computed(() => {
    const customer = process.value?.draft?.customer
    if (!customer) return false
    if (customer.brevoActive === false) return true
    return typeof customer.brevoEmail === 'string' && customer.brevoEmail.trim().length > 0
  })

  // The recovery contacts of the current draft, preferring the dedicated state
  // field over the copy inside the draft.
  const recoveryContacts = computed<RecoveryContact[]>(() => {
    return process.value?.recoveryContacts ?? process.value?.draft?.customer?.recoveryContacts ?? []
  })

  const verifiedRecoveryContacts = computed(() => recoveryContacts.value.filter((c) => c.verified))
  const pendingRecoveryContacts = computed(() => recoveryContacts.value.filter((c) => !c.verified))

  // Only a contact that completed the double opt-in counts. An unverified
  // address never satisfies the activation gate.
  const hasVerifiedRecoveryContact = computed(() => verifiedRecoveryContacts.value.length > 0)

  const recoveryContactsRequired = computed(() => process.value?.recoveryContactsRequired !== false)

  const recoveryCodesRemaining = computed(() => process.value?.recoveryCodesRemaining ?? 0)

  // recoveryContactHint is the German explanation of the security channel and
  // how it differs from the broad Brevo distribution address.
  const recoveryContactHint =
    'Wiederherstellungs-Adressen sind der Sicherheitskanal Ihres Unternehmens: nur an diese Adressen werden Links zur Wiederherstellung des Zugangs und zum Zurücksetzen einer Änderung verschickt. Die Benachrichtigungs-E-Mail-Adresse ist dagegen ein breiter Verteiler und erhält ausschließlich Statusinformationen ohne Aktionslinks.'

  const canActivate = computed(() => {
    if (!process.value) return false
    if (
      process.value.status === 'COMPLETED' ||
      process.value.status === 'FAILED' ||
      process.value.status === 'EXPIRED'
    ) {
      return false
    }
    if (!hasValidTenants.value) return false
    if (!hasNotificationEmail.value) return false
    // A recovery process cannot change the contact list at all, so it is exempt
    // from the gate; every other process must have a verified contact.
    if (
      recoveryContactsRequired.value &&
      normalizeProcessType(process.value.type) !== 'OIDC_RECOVERY' &&
      !hasVerifiedRecoveryContact.value
    ) {
      return false
    }
    if (requiresOIDC.value) {
      return isOIDCVerified.value && isAdminProven.value
    }
    return true
  })

  async function peekInvitation(token: string): Promise<InvitationPeek> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.get<InvitationPeek>('/api/registry/v1/onboarding/invitation', {
        params: { t: token },
        withCredentials: true,
      })
      invitation.value = resp.data
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  async function redeemInvitation(token: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await axios.post(
        '/api/registry/v1/onboarding/redeem',
        { token },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        },
      )
    } catch (err) {
      // Axios might follow 302 or get 200. If it throws, map error:
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  async function fetchState(): Promise<OnboardingProcess> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.get<OnboardingProcess>('/api/registry/v1/onboarding/state', {
        withCredentials: true,
      })
      process.value = resp.data
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // refreshProcess silently reloads the onboarding process. Used after draft
  // updates that make the server re-evaluate derived state (e.g. adminProven).
  // Failures are ignored on purpose: the locally applied draft stays usable.
  async function refreshProcess(): Promise<void> {
    try {
      const resp = await axios.get<OnboardingProcess>('/api/registry/v1/onboarding/state', {
        withCredentials: true,
      })
      process.value = resp.data
    } catch {
      // keep locally applied draft state
    }
  }

  async function saveOIDC(oidcDraft: OIDCDraft): Promise<OIDCDiscoveryResult> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.put<OIDCDiscoveryResult>(
        '/api/registry/v1/onboarding/oidc',
        oidcDraft,
        {
          withCredentials: true,
        },
      )
      discoveryResult.value = resp.data
      if (process.value) {
        process.value.draft.oidc = { ...oidcDraft }
      }
      stepUpRequired.value = false
      stepUpReference.value = undefined
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      // A real identity provider change without a valid grant is refused and the
      // server-side draft stays untouched, so the wizard can simply ask for the
      // step-up and repeat the request with the unchanged input.
      if (mapped.code === 'ERR_STEP_UP_REQUIRED') {
        stepUpRequired.value = true
        stepUpReference.value = mapped.reference
      }
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // saveLoginMode stores the login method of the draft. Switching the method of
  // an onboarded customer needs the same step-up grant as an identity provider
  // change, which surfaces exactly like in saveOIDC.
  async function saveLoginMode(mode: 'oidc' | 'local'): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await axios.put(
        '/api/registry/v1/onboarding/login-mode',
        { loginMode: mode },
        { withCredentials: true },
      )
      if (process.value) {
        process.value.loginMode = mode
      }
      stepUpRequired.value = false
      stepUpReference.value = undefined
    } catch (err) {
      const mapped = mapGermanError(err)
      if (mapped.code === 'ERR_STEP_UP_REQUIRED') {
        stepUpRequired.value = true
        stepUpReference.value = mapped.reference
      }
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // requestStepUp obtains the OIDC_CONFIGURATION_CHANGE grant for the running
  // process from the authenticated Portal session. It fails with
  // ERR_REAUTHENTICATION_REQUIRED when that session is no longer recent enough.
  async function requestStepUp(): Promise<void> {
    const onboardingId = process.value?.id
    if (!onboardingId) {
      const mapped: AppError = {
        code: 'ERR_NOT_FOUND',
        message: 'Der Vorgang ist nicht mehr verf\u00fcgbar. Bitte laden Sie die Seite neu.',
      }
      error.value = mapped
      throw mapped
    }

    stepUpPending.value = true
    error.value = null
    try {
      await axios.post(
        '/api/registry/v1/onboarding/step-up',
        { onboardingId },
        { withCredentials: true },
      )
      reauthenticationRequired.value = false
    } catch (err) {
      const mapped = mapGermanError(err)
      if (mapped.code === 'ERR_REAUTHENTICATION_REQUIRED') {
        reauthenticationRequired.value = true
      }
      error.value = mapped
      throw mapped
    } finally {
      stepUpPending.value = false
    }
  }

  // confirmStepUpAndSaveOIDC is what the German step-up dialog calls: it obtains
  // the grant and immediately retries the save that was refused, so the customer
  // never has to re-enter the identity provider data.
  async function confirmStepUpAndSaveOIDC(oidcDraft: OIDCDraft): Promise<OIDCDiscoveryResult> {
    await requestStepUp()
    return saveOIDC(oidcDraft)
  }

  // Recovery-contact changes use the same process-scoped privilege as OIDC
  // changes. Keeping the grant-and-retry sequence here ensures the wizard
  // repeats exactly the contact list that the server refused before step-up.
  async function confirmStepUpAndSaveRecoveryContacts(
    contacts: string[],
  ): Promise<RecoveryContactsResult> {
    await requestStepUp()
    return saveRecoveryContacts(contacts)
  }

  async function confirmStepUpAndSaveLoginMode(mode: 'oidc' | 'local'): Promise<void> {
    await requestStepUp()
    return saveLoginMode(mode)
  }

  function dismissStepUp(): void {
    stepUpRequired.value = false
    stepUpReference.value = undefined
    reauthenticationRequired.value = false
  }

  async function fetchRedirectUris(): Promise<OnboardingRedirectUris> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.get<OnboardingRedirectUris>(
        '/api/registry/v1/onboarding/redirect-uris',
        {
          withCredentials: true,
        },
      )
      redirectUris.value = resp.data
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  async function startOIDCVerify(): Promise<string> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.post<{ authorizationUrl: string }>(
        '/api/registry/v1/onboarding/oidc/verify',
        {},
        { withCredentials: true },
      )
      return resp.data.authorizationUrl
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  async function saveTenants(tenants: TenantDraft[]): Promise<TenantDraft[]> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.put<{ tenants: TenantDraft[] } | TenantDraft[]>(
        '/api/registry/v1/onboarding/tenants',
        { tenants },
        { withCredentials: true },
      )
      const savedTenants = Array.isArray(resp.data) ? resp.data : resp.data.tenants || tenants
      if (process.value) {
        process.value.draft.tenants = savedTenants
      }
      // The backend re-evaluates the OIDC verification (notably adminProven)
      // against the updated draft, so refresh the process to avoid a stale
      // activation gate.
      await refreshProcess()
      return savedTenants
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  async function savePermissions(permissions: PermissionDraft[]): Promise<PermissionDraft[]> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.put<{ permissions: PermissionDraft[] } | PermissionDraft[]>(
        '/api/registry/v1/onboarding/permissions',
        { permissions },
        { withCredentials: true },
      )
      const saved = Array.isArray(resp.data) ? resp.data : resp.data.permissions || permissions
      if (process.value) {
        process.value.draft.permissions = saved
      }
      // Group-role mappings decide whether TENANT_ADMIN access is proven, so the
      // server-side re-evaluation must be reflected before the review step.
      await refreshProcess()
      return saved
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  async function saveContact(
    brevoEmail: string,
    brevoActive: boolean,
  ): Promise<{ brevoEmail: string; brevoActive: boolean }> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.put<{ brevoEmail: string; brevoActive: boolean }>(
        '/api/registry/v1/onboarding/contact',
        { brevoEmail, brevoActive },
        { withCredentials: true },
      )
      if (process.value) {
        process.value.draft.customer = {
          ...process.value.draft.customer,
          brevoEmail: resp.data.brevoEmail,
          brevoActive: resp.data.brevoActive,
        }
      }
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // saveCustomerAdminMapping stores the customer-wide group -> CUSTOMER_ADMIN
  // mapping. Groups mapped here receive customer admin access across every tenant
  // of the customer; leaving it empty keeps the TENANT_ADMIN fallback.
  async function saveCustomerAdminMapping(
    groupRoleMapping: Record<string, string>,
    groupNameMapping: Record<string, string>,
  ): Promise<{ groupRoleMapping: Record<string, string>; groupNameMapping: Record<string, string> }> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.put<{
        groupRoleMapping?: Record<string, string>
        groupNameMapping?: Record<string, string>
      }>(
        '/api/registry/v1/onboarding/customer-admin-mapping',
        { groupRoleMapping, groupNameMapping },
        { withCredentials: true },
      )
      const savedRoleMapping = resp.data.groupRoleMapping || groupRoleMapping
      const savedNameMapping = resp.data.groupNameMapping || groupNameMapping
      if (process.value) {
        process.value.draft.customer = {
          ...process.value.draft.customer,
          groupRoleMapping: savedRoleMapping,
          groupNameMapping: savedNameMapping,
        }
      }
      return { groupRoleMapping: savedRoleMapping, groupNameMapping: savedNameMapping }
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // saveRecoveryContacts stores the submitted addresses and triggers a fresh
  // double opt-in mail for every address that is not verified yet. Outside the
  // initial onboarding the server demands a step-up grant, which is surfaced as
  // ERR_STEP_UP_REQUIRED exactly like an identity provider change.
  async function saveRecoveryContacts(contacts: string[]): Promise<RecoveryContactsResult> {
    loading.value = true
    error.value = null
    stepUpRequired.value = false
    stepUpReference.value = undefined
    try {
      const resp = await axios.put<RecoveryContactsResult>(
        '/api/registry/v1/onboarding/recovery-contacts',
        { contacts },
        { withCredentials: true },
      )
      if (process.value) {
        process.value.recoveryContacts = resp.data.contacts
        process.value.recoveryContactsVerified = resp.data.verifiedCount
        process.value.draft.customer = {
          ...process.value.draft.customer,
          recoveryContacts: resp.data.contacts,
        }
      }
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      if (mapped.code === 'ERR_STEP_UP_REQUIRED') {
        stepUpRequired.value = true
        stepUpReference.value = mapped.reference
      }
      if (mapped.code === 'ERR_REAUTHENTICATION_REQUIRED') {
        reauthenticationRequired.value = true
      }
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // resendRecoveryContactVerification mails a fresh single-use confirmation link
  // to every address that is still unverified.
  async function resendRecoveryContactVerification(): Promise<RecoveryContactsResult> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.post<RecoveryContactsResult>(
        '/api/registry/v1/onboarding/recovery-contacts/resend',
        {},
        { withCredentials: true },
      )
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // confirmRecoveryContact redeems the public double opt-in link. It is
  // single-use, so opening it twice reports a generic failure.
  async function confirmRecoveryContact(
    token: string,
  ): Promise<{ customerNo: string; contact: string }> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.get<{ customerNo: string; contact: string }>(
        '/api/registry/v1/onboarding/recovery-contacts/verify',
        { params: { t: token } },
      )
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // saveCustomerRecoveryContacts updates the customer's recovery contacts outside
  // the onboarding wizard. The server demands customer-admin scope and a step-up grant.
  async function saveCustomerRecoveryContacts(
    customerNo: string,
    contacts: string[],
  ): Promise<RecoveryContactsResult> {
    loading.value = true
    error.value = null
    stepUpRequired.value = false
    stepUpReference.value = undefined
    try {
      const resp = await axios.put<RecoveryContactsResult>(
        `/api/registry/v1/customers/${customerNo}/recovery-contacts`,
        { contacts },
        { withCredentials: true },
      )
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      if (mapped.code === 'ERR_STEP_UP_REQUIRED') {
        stepUpRequired.value = true
        stepUpReference.value = mapped.reference
      }
      if (mapped.code === 'ERR_REAUTHENTICATION_REQUIRED') {
        reauthenticationRequired.value = true
      }
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // confirmStepUpAndSaveCustomerRecoveryContacts obtains the customer-scoped grant
  // and retries the recovery contact update.
  async function confirmStepUpAndSaveCustomerRecoveryContacts(
    customerNo: string,
    contacts: string[],
  ): Promise<RecoveryContactsResult> {
    await requestStepUpForCustomer(customerNo)
    return saveCustomerRecoveryContacts(customerNo, contacts)
  }

  // resendCustomerRecoveryContactVerification triggers fresh double opt-in emails
  // for all unverified recovery contacts of a customer outside the wizard.
  async function resendCustomerRecoveryContactVerification(
    customerNo: string,
  ): Promise<RecoveryContactsResult> {
    loading.value = true
    error.value = null
    stepUpRequired.value = false
    stepUpReference.value = undefined
    try {
      const resp = await axios.post<RecoveryContactsResult>(
        `/api/registry/v1/customers/${customerNo}/recovery-contacts/resend`,
        {},
        { withCredentials: true },
      )
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      if (mapped.code === 'ERR_STEP_UP_REQUIRED') {
        stepUpRequired.value = true
        stepUpReference.value = mapped.reference
      }
      if (mapped.code === 'ERR_REAUTHENTICATION_REQUIRED') {
        reauthenticationRequired.value = true
      }
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // confirmStepUpAndResendCustomerRecoveryContactVerification obtains the grant
  // and retries the resend action.
  async function confirmStepUpAndResendCustomerRecoveryContactVerification(
    customerNo: string,
  ): Promise<RecoveryContactsResult> {
    await requestStepUpForCustomer(customerNo)
    return resendCustomerRecoveryContactVerification(customerNo)
  }

  // regenerateRecoveryCodes replaces the code set of the customer. It needs
  // customer-admin scope plus a step-up grant, so the caller has to obtain the
  // grant through requestStepUpForCustomer first.
  async function regenerateRecoveryCodes(customerNo: string): Promise<RecoveryCodesResult> {
    loading.value = true
    error.value = null
    stepUpRequired.value = false
    try {
      const resp = await axios.post<RecoveryCodesResult>(
        `/api/registry/v1/customers/${customerNo}/recovery-codes`,
        {},
        { withCredentials: true },
      )
      recoveryCodes.value = resp.data
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      if (mapped.code === 'ERR_STEP_UP_REQUIRED') {
        stepUpRequired.value = true
        stepUpReference.value = mapped.reference
      }
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // requestStepUpForCustomer obtains a step-up grant that is bound to the
  // customer instead of to a running wizard, which is what the Portal
  // administration needs for a code regeneration.
  async function requestStepUpForCustomer(customerNo: string): Promise<void> {
    reauthenticationRequired.value = false
    stepUpPending.value = true
    try {
      await axios.post(
        '/api/registry/v1/onboarding/step-up',
        { customerNo },
        { withCredentials: true },
      )
      stepUpRequired.value = false
      stepUpReference.value = undefined
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      if (mapped.code === 'ERR_REAUTHENTICATION_REQUIRED') {
        reauthenticationRequired.value = true
      }
      throw mapped
    } finally {
      stepUpPending.value = false
    }
  }

  // confirmStepUpAndRegenerateRecoveryCodes obtains the grant and immediately
  // retries the refused regeneration.
  async function confirmStepUpAndRegenerateRecoveryCodes(
    customerNo: string,
  ): Promise<RecoveryCodesResult> {
    await requestStepUpForCustomer(customerNo)
    return regenerateRecoveryCodes(customerNo)
  }

  // downloadRecoveryCodes offers the codes as a plain text file. The codes are
  // shown only once, so the customer must be able to store them immediately.
  function downloadRecoveryCodes(codes?: RecoveryCodesResult | null): void {
    const payload = codes ?? recoveryCodes.value
    if (!payload || !payload.fileContent) return
    const blob = new Blob([payload.fileContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = payload.fileName || 'wiederherstellungscodes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  async function activate(): Promise<{ success: boolean; customerNo: string; status: string }> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.post<{
        success: boolean
        customerNo: string
        status: string
        recoveryCodes?: string[]
        recoveryCodesFileName?: string
        recoveryCodesFileContent?: string
      }>('/api/registry/v1/onboarding/activate', {}, { withCredentials: true })
      if (process.value) {
        process.value.status = 'COMPLETED'
      }
      // The codes exist in this response only. They are kept in memory so the
      // completion screen can show them once and offer the download.
      if (resp.data.recoveryCodes?.length) {
        recoveryCodes.value = {
          codes: resp.data.recoveryCodes,
          remaining: resp.data.recoveryCodes.length,
          fileName: resp.data.recoveryCodesFileName || 'wiederherstellungscodes.txt',
          fileContent: resp.data.recoveryCodesFileContent || resp.data.recoveryCodes.join('\n'),
        }
      }
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // startCustomerProvisioning starts a provisioning process. Legacy change
  // types are still accepted and normalized to SELF_SERVICE by the server.
  async function startCustomerProvisioning(
    customerNo: string,
    type?: RequestedOnboardingType,
    tenantName?: string,
    options?: { delivery?: ProvisioningDelivery; adminEmail?: string },
  ): Promise<ProvisioningStartResult> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.post<ProvisioningStartResult>(
        `/api/registry/v1/customers/${customerNo}/provisioning`,
        {
          type,
          tenantName,
          delivery: options?.delivery,
          adminEmail: options?.adminEmail,
        },
        { withCredentials: true },
      )
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  // startSelfService opens the unified configuration wizard for the customer in
  // the current Portal session; no invitation mail and no process type are
  // involved, the server resolves the session path to SELF_SERVICE.
  async function startSelfService(
    customerNo: string,
    tenantName?: string,
  ): Promise<ProvisioningStartResult> {
    return startCustomerProvisioning(customerNo, undefined, tenantName, {
      delivery: 'SESSION',
    })
  }

  // startOIDCRecovery is a portal-admin only flow: it always sends a fresh
  // invitation to the customer instead of opening the wizard locally.
  async function startOIDCRecovery(
    customerNo: string,
    adminEmail: string,
  ): Promise<ProvisioningStartResult> {
    loading.value = true
    error.value = null
    try {
      const resp = await axios.post<ProvisioningStartResult>(
        `/api/registry/v1/customers/${customerNo}/provisioning/recovery`,
        { adminEmail },
        { withCredentials: true },
      )
      return resp.data
    } catch (err) {
      const mapped = mapGermanError(err)
      error.value = mapped
      throw mapped
    } finally {
      loading.value = false
    }
  }

  return {
    process,
    invitation,
    discoveryResult,
    redirectUris,
    loading,
    error,
    requiresOIDC,
    editableSections,
    canEditContact,
    canEditOIDC,
    canEditTenants,
    canEditPermissions,
    canEditAnyPermission,
    lockedSectionHint,
    isOIDCVerified,
    isAdminProven,
    hasValidTenants,
    hasNotificationEmail,
    recoveryContacts,
    verifiedRecoveryContacts,
    pendingRecoveryContacts,
    hasVerifiedRecoveryContact,
    recoveryContactsRequired,
    recoveryCodesRemaining,
    recoveryContactHint,
    recoveryCodes,
    saveRecoveryContacts,
    saveCustomerRecoveryContacts,
    resendRecoveryContactVerification,
    resendCustomerRecoveryContactVerification,
    confirmRecoveryContact,
    regenerateRecoveryCodes,
    requestStepUpForCustomer,
    confirmStepUpAndRegenerateRecoveryCodes,
    confirmStepUpAndSaveCustomerRecoveryContacts,
    confirmStepUpAndResendCustomerRecoveryContactVerification,
    downloadRecoveryCodes,
    canActivate,
    peekInvitation,
    redeemInvitation,
    fetchState,
    saveOIDC,
    saveLoginMode,
    confirmStepUpAndSaveLoginMode,
    stepUpRequired,
    stepUpReference,
    stepUpPending,
    reauthenticationRequired,
    requestStepUp,
    confirmStepUpAndSaveOIDC,
    confirmStepUpAndSaveRecoveryContacts,
    dismissStepUp,
    fetchRedirectUris,
    startOIDCVerify,
    saveTenants,
    savePermissions,
    saveContact,
    saveCustomerAdminMapping,
    activate,
    startCustomerProvisioning,
    startSelfService,
    startOIDCRecovery,
  }
}
